import { Component, Input, Output, EventEmitter, OnInit, SimpleChanges, ElementRef, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { enumCompanyPaymentType } from '@app/data-layer/enums/companies';
import { enumDocumentType } from '@app/data-layer/enums/documents';
import { enumBillItemUnit } from '@app/data-layer/enums/lcl';
import { enumBillItemProductCode, enumPaymentState } from '@app/data-layer/enums/payments';
import { CompanyModel } from '@app/data-layer/models/companies';
import { DocumentModel } from '@app/data-layer/models/documents';
import { BillItemModel, BillModel, PaymentMethodModel, RefundItemModel, RefundModel } from '@app/data-layer/models/payments';
import { UserBetaFlagModel } from '@app/data-layer/models/user';
import { DocumentsRepository } from '@app/data-layer/repositories';
import { UserBillingSelectors } from '@app/modules/company/state/billing';
import { JwtHelperService } from '@auth0/angular-jwt';
import { INPUT_TYPES_MAP, InputTypeMap } from '@constants/input-types-map';
import { ACCESS_TOKEN } from '@constants/setting-keys.const';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { Select } from '@ngxs/store';
import { CompaniesService } from '@services/companies.service';
import { GenericService } from '@services/generic.service';
import { BillListService } from '@shared/bill-shared/services';
import { Observable, ReplaySubject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-billing-history-table-user',
  templateUrl: './billing-history-table-user.component.html',
  styleUrls: ['./billing-history-table-user.component.scss'],
})
export class BillingHistoryTableUserComponent implements OnInit {
  @Select(UserBillingSelectors.isLoading) public isLoading$: Observable<boolean>;
  @Select(UserBillingSelectors.getBillList) public billList$: Observable<BillModel[]>;
  @Select(UserBillingSelectors.getTotal) public total$: Observable<number>;

  @Input() public companies: CompanyModel[];
  @Input() public tableId: string | number = 'isf-list-admin';
  @Input() public itemIds: number[];
  @Input() public paymentMethods: PaymentMethodModel[] = [];
  @Output() public loadMoreItems: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('invoiceDownloadLink', { static: false }) public invoiceDownloadLinkRef: ElementRef;

  public formatDateTime: string = 'MM/dd/yyyy';
  public formatMonthDateTime: string = 'MMMM';
  public paginationMaxSize$: Observable<number>;
  public openFlags: Map<number, boolean> = new Map();
  public enumCompanyPaymentType: typeof enumCompanyPaymentType = enumCompanyPaymentType;
  public enumBillItemProductCode: typeof enumBillItemProductCode = enumBillItemProductCode;
  public enumPaymentState: typeof enumPaymentState = enumPaymentState;
  public openInsertInvoiceModal: boolean = false;
  public openRefundModal: boolean = false;
  public openManualPaidModal: boolean = false;
  public CUSTOM_MASK = CUSTOM_MASK;
  public insertInvoiceForm: UntypedFormGroup = new UntypedFormGroup({
    timestamp: new UntypedFormControl(null, Validators.required),
    documentNumber: new UntypedFormControl(null, Validators.required),
    receiptNumber: new UntypedFormControl(null, Validators.required),
    billId: new UntypedFormControl(null, Validators.required),
  });
  public refundForm: UntypedFormGroup = new UntypedFormGroup({
    refundTransactionId: new UntypedFormControl(null, Validators.required),
    documentNumber: new UntypedFormControl(null, Validators.required),
    receiptNumber: new UntypedFormControl(null, Validators.required),
    timestamp: new UntypedFormControl(null, Validators.required),
  });
  public manualPaidForm: UntypedFormGroup = new UntypedFormGroup({
    paidDate: new UntypedFormControl(null),
    transactionNumber: new UntypedFormControl(null),
    comment: new UntypedFormControl(null),
  });
  public integerInputMap: InputTypeMap = INPUT_TYPES_MAP.INTEGER;
  public errors = {
    required: 'FORM_ERROR.REQUIRED',
  };
  public isTablet$: Observable<boolean>;
  public selectedBill: BillModel;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private genericService: GenericService,
    private documentsRepository: DocumentsRepository,
    private jwtHelperService: JwtHelperService,
    private companiesService: CompaniesService,
    private billListService: BillListService,
  ) {
    this.paginationMaxSize$ = this.genericService.isMobile$.pipe(map((isMobile) => (isMobile ? 5 : 7)));
  }

  public ngOnInit(): void {
    this.isTablet$ = this.genericService.isTablet$;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.itemIds) {
      this.updateOpenMap();
    }
  }

  public loadMore() {
    this.loadMoreItems.emit();
  }

  public onDownloadInvoice(event: Event, billId: number, targetInvoiceId: number, isInvoiceDocumentDownloaded: boolean) {
    event.stopPropagation();

    if (!isInvoiceDocumentDownloaded) {
      return;
    }

    this.documentsRepository
      .getBillDocuments(billId, false)
      .pipe(take(1))
      .subscribe((files: DocumentModel[]) => {
        const billInvoiceFile: DocumentModel = files
          .filter((item: DocumentModel) => item.type === enumDocumentType.SAP_INVOICE)
          .find((item: DocumentModel) => item.relatedEntityId === targetInvoiceId);
        if (!!billInvoiceFile) {
          this.downloadDocument(billInvoiceFile.id);
        }
      });
  }

  public get isOpenedAll(): boolean {
    const values: boolean[] = Array.from(this.openFlags.values());
    if (values.length === 0) {
      return false;
    }
    return values.every((flag) => flag);
  }

  public get isAdminOrSuper(): boolean {
    return this.companiesService.isAdminOrSuper();
  }

  public totalForBill(bill: BillModel, excludePartiallyRefunded: boolean = false): number {
    const totalBillAmount = bill.items && bill.items.length ? bill.items.reduce((prev, curr) => curr.quantity * curr.price + prev, 0) : 0;

    if (excludePartiallyRefunded && bill.state !== enumPaymentState.REFUNDED) {
      const billRefundItems = this.getBillRefundItems(bill);

      const totalPartialRefundsAmount = billRefundItems.length
        ? billRefundItems.reduce((prev, curr) => {
            const billItem = bill.items.find((item) => item.id === curr.billItemId);
            return !!billItem ? curr.amount * curr.quantity + prev : 0;
          }, 0)
        : 0;

      return !!this.isHasRefundInvoice(bill) ? totalBillAmount : totalBillAmount - totalPartialRefundsAmount || totalBillAmount;
    }

    return totalBillAmount;
  }

  public billItemUnitName(unit: enumBillItemUnit): string {
    switch (unit) {
      case enumBillItemUnit.CBM:
        return 'LENGTH_UNIT.CBM';
      case enumBillItemUnit.HBL:
        return 'LABELS.HBL';
      case enumBillItemUnit.TON:
        return 'WEIGHT_UNIT.TON';
      default:
        return '';
    }
  }

  public redirect(referenceNumber: string, companyId: number, event: Event): void {
    event.stopPropagation();
    const replacedReference: string = referenceNumber.replace(/\d+/gi, '');
    switch (replacedReference) {
      case 'ES':
      case 'ISF':
      case 'BR':
      case 'SH':
        const selectedCompany = this.companies.find((company) => company.id === companyId);
        this.companiesService.setDefaultCompany(selectedCompany);

        this.companiesService
          .getCompanyAsync(true, true, companyId)
          .pipe(
            switchMap((company) => this.loadUserBetaFlags(company)),
            takeUntil(this.destroy$),
          )
          .subscribe((betaFlags) => {
            this.companiesService.setUserBetaFlags(betaFlags);
            this.billListService.redirect(referenceNumber, companyId, 'control-defaultBondReferenceNumber');
          });
        break;
      default:
        break;
    }
  }

  public isHasRefundInvoice(bill: BillModel): boolean {
    return !!bill?.refundInvoiceId && !!bill?.refundInvoiceDocumentNumber && !!bill?.refundTimestamp;
  }

  public getBillRefundItems(bill: BillModel): RefundItemModel[] {
    const refunds = bill?.refunds || [];
    const billRefundItems: RefundItemModel[] = [];
    refunds.forEach((refund) => {
      billRefundItems.push(...refund.refundItems);
    });

    return billRefundItems;
  }

  public getBillItemBillRefunds(bill: BillModel, item: BillItemModel): RefundModel[] {
    const currentBillItemRefunds = (bill.refunds || []).filter((refund) => refund.refundItems.some((ri) => ri.billItemId === item.id));
    return currentBillItemRefunds;
  }

  public getRefundBillItemRefundItem(refund: RefundModel, item: BillItemModel): RefundItemModel {
    const currentBillItemRefundItem = (refund?.refundItems || []).find((refundItem) => refundItem.billItemId === item.id);
    return currentBillItemRefundItem;
  }

  public getMarineInsuranceIdByReferenceNumber(referenceNumber: string): void {
    this.billListService.getMarineInsuranceIdByReferenceNumber(referenceNumber);
  }

  public getBillCreatedDate(bill: BillModel): string {
    if (!!bill?.invoiceTimestamp) {
      return bill?.invoiceTimestamp || '';
    }

    return bill?.timestamp || '';
  }

  public paymentMethodName(codeId: number): string {
    return (this.paymentMethods || []).find((item: PaymentMethodModel) => item.codeId === codeId)?.name || codeId.toString();
  }

  public getProductCodeTranslationName(billItem: BillItemModel): string {
    const keys = Object.keys(enumBillItemProductCode);
    const values: string[] = Object.values(enumBillItemProductCode);

    const index = values.indexOf(billItem?.productCode);
    if (index !== -1) {
      return `PRODUCT_CODE.${keys[index]}`;
    }

    return billItem?.description || '';
  }

  public getPaymentMethodPropertyValue(paymentCodeId: number, fieldname: string): string {
    const currentPaymentMethodObject = (this.paymentMethods || []).find((item: PaymentMethodModel) => item.codeId === paymentCodeId);
    return !!currentPaymentMethodObject ? currentPaymentMethodObject[fieldname] : '';
  }

  public getPaymentStateBadgeClass(state: enumPaymentState) {
    switch (state) {
      case enumPaymentState.CREATED:
      case enumPaymentState.CONFIRMED:
      case enumPaymentState.NOT_VALIDATED:
      case enumPaymentState.CANCELED:
      case enumPaymentState.OUTDATED:
      case enumPaymentState.EXECUTE_PENDING:
      default:
        //gray outline
        return 'state-badge-outlined-secondary';
      case enumPaymentState.FAILED:
      case enumPaymentState.MULTI_FAILED:
        //red outline
        return 'state-badge-outlined-danger';
      case enumPaymentState.PENDING:
      case enumPaymentState.DELAYED:
        //yellow outline
        return 'state-badge-outlined-warning';
      case enumPaymentState.SUCCESS:
      case enumPaymentState.PARTIALLY_REFUNDED:
        //green filled
        return 'state-badge-success';
      case enumPaymentState.REFUNDED:
        //red filled
        return 'state-badge-warning';
    }
  }

  public getPaymentStateIconPath(state: enumPaymentState) {
    switch (state) {
      case enumPaymentState.SUCCESS:
        return 'assets/icons/check_small.svg';
      case enumPaymentState.PARTIALLY_REFUNDED:
        return 'assets/icons/icon_arrow_return_white.svg';

      default:
        return '';
    }
  }

  public getPaymentStateTranslationText(state: enumPaymentState, providerType: enumCompanyPaymentType) {
    const defaultTranslationText = 'PAYMENT_STATE.' + enumPaymentState[state];
    switch (state) {
      case enumPaymentState.PARTIALLY_REFUNDED:
      case enumPaymentState.SUCCESS:
        return 'LABELS.PAID';
      case enumPaymentState.DELAYED:
        if (providerType === enumCompanyPaymentType.MONTHLY) {
          return 'LABELS.AWAITS_PAYMENT';
        } else {
          return defaultTranslationText;
        }
      default:
        return defaultTranslationText;
    }
  }

  public isReferenceNumberRedirectable(referenceNumber: string): boolean {
    const replacedReference: string = referenceNumber.replace(/\d+/gi, '');
    switch (replacedReference) {
      case 'ES':
      case 'ISF':
      case 'BR':
      case 'SH':
        return true;
      default:
        return false;
    }
  }

  private loadUserBetaFlags(company?: CompanyModel): Observable<UserBetaFlagModel[]> {
    return this.companiesService.getUserBetaFlags({
      userExternalId: this.userExternalId,
      companyId: company.id,
      organizationId: company.organizationId,
    });
  }

  private get userExternalId(): string {
    return this.jwtHelperService.decodeToken(localStorage.getItem(ACCESS_TOKEN))?.user_external_id || null;
  }

  private updateOpenMap() {
    const isOpenedAll: boolean = this.isOpenedAll;
    if (this.itemIds && this.itemIds.length) {
      this.itemIds.forEach((id) => {
        if (!this.openFlags.has(id)) {
          this.openFlags.set(id, isOpenedAll);
        }
      });
    }
  }

  private downloadDocument(id: number): void {
    this.documentsRepository
      .getDownloadLink(id)
      .pipe(take(1))
      .subscribe((linkData: { downloadUrl: string }) => {
        this.invoiceDownloadLinkRef.nativeElement.href = linkData.downloadUrl;
        this.invoiceDownloadLinkRef.nativeElement.click();
      });
  }
}
